/**
 * Утилиты для работы с датами
 */

const MONTH_NAMES_GENITIVE = [
  "января",
  "февраля",
  "марта",
  "апреля",
  "мая",
  "июня",
  "июля",
  "августа",
  "сентября",
  "октября",
  "ноября",
  "декабря",
];

const MONTHS_MAP: Record<string, number> = {
  января: 0,
  февраля: 1,
  марта: 2,
  апреля: 3,
  мая: 4,
  июня: 5,
  июля: 6,
  августа: 7,
  сентября: 8,
  октября: 9,
  ноября: 10,
  декабря: 11,
};

/**
 * Парсит дату из строки в различных форматах
 * Поддерживает: DD.MM.YYYY, YYYY-MM-DD, DD месяц YYYY
 */
export function parseDate(dateStr: string): Date | null {
  if (!dateStr) return null;

  // Формат DD.MM.YYYY
  const dotMatch = dateStr.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
  if (dotMatch) {
    const date = new Date(parseInt(dotMatch[3]), parseInt(dotMatch[2]) - 1, parseInt(dotMatch[1]));
    return isNaN(date.getTime()) ? null : date;
  }

  // Формат YYYY-MM-DD
  const isoMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const date = new Date(parseInt(isoMatch[1]), parseInt(isoMatch[2]) - 1, parseInt(isoMatch[3]));
    return isNaN(date.getTime()) ? null : date;
  }

  // Формат DD месяц YYYY
  const russianMatch = dateStr.match(/^(\d{1,2})\s+(\S+)\s+(\d{4})/);
  if (russianMatch && MONTHS_MAP[russianMatch[2].toLowerCase()] !== undefined) {
    const date = new Date(
      parseInt(russianMatch[3]),
      MONTHS_MAP[russianMatch[2].toLowerCase()],
      parseInt(russianMatch[1]),
    );
    return isNaN(date.getTime()) ? null : date;
  }

  return null;
}

/**
 * Извлекает 4-значный год из даты в любом из поддерживаемых форматов.
 *
 * Безопасная замена `birthDate.slice(0, 4)`, которая на «22.09.1984» возвращает
 * мусор «22.0». Сначала пытается parseDate, затем фоллбек на regex \d{4}.
 *
 * @returns строка-год «1984» или null если ничего не нашлось.
 */
export function extractBirthYear(dateStr: string | null | undefined): string | null {
  if (!dateStr) return null;
  const parsed = parseDate(dateStr);
  if (parsed) return String(parsed.getFullYear());
  const match = dateStr.match(/(\d{4})/);
  return match ? match[1] : null;
}

/**
 * Вычисляет возраст по дате рождения
 */
export function calculateAge(birthDateStr: string): number | null {
  const birthDate = parseDate(birthDateStr);
  if (!birthDate) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  return age >= 0 ? age : null;
}

/**
 * Форматирует дату в русский формат "28 августа 1985 года"
 */
export function formatDateRussian(dateStr: string): string {
  const date = parseDate(dateStr);
  if (!date) return dateStr;

  const day = date.getDate();
  const month = MONTH_NAMES_GENITIVE[date.getMonth()];
  const year = date.getFullYear();

  return `${day} ${month} ${year} года`;
}

/**
 * Склонение слова "год" в зависимости от числа
 */
export function pluralizeYears(age: number): string {
  const lastDigit = age % 10;
  const lastTwoDigits = age % 100;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) {
    return "лет";
  }

  if (lastDigit === 1) {
    return "год";
  }

  if (lastDigit >= 2 && lastDigit <= 4) {
    return "года";
  }

  return "лет";
}

/**
 * Форматирует дату с возрастом: "28 августа 1985 года" + "40 лет"
 */
export function formatDateWithAge(dateStr: string): { date: string; age: string | null } {
  const formattedDate = formatDateRussian(dateStr);
  const age = calculateAge(dateStr);

  return {
    date: formattedDate,
    age: age !== null ? `${age} ${pluralizeYears(age)}` : null,
  };
}

/**
 * Форматирует дату в ISO формат YYYY-MM-DD
 * Поддерживает входные форматы: DD.MM.YYYY, YYYY-MM-DD, DD месяц YYYY
 */
export function formatDateToISO(dateStr: string): string {
  if (!dateStr) return "";

  // Если уже в ISO формате
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    return dateStr;
  }

  const date = parseDate(dateStr);
  if (!date) return dateStr;

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

/**
 * Приводит дату к виду ДД.ММ.ГГГГ, если она распознаётся.
 *
 * Карточки результатов печатают то, что пришло из модуля, а модули отдают
 * дату по-разному: ФСИН — «1982-09-30», Интерпол — «31.03.1974». В карточке
 * рядом с русским текстом ISO-дата выглядит недоделкой, а разбирать её
 * глазами неудобно. Нераспознанное значение возвращается как есть: пустая
 * строка или «не указано» из источника не должны превращаться в «Invalid
 * Date».
 */
export function formatDateShort(dateStr: string | null | undefined): string {
  if (!dateStr) return "";
  const date = parseDate(String(dateStr));
  if (!date) return String(dateStr);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getFullYear()}`;
}

/**
 * Дата из ответа, пригодная для показа: ДД.ММ.ГГГГ либо «—».
 *
 * Поставщик кредитных данных обозначает «нет такой даты» девятками —
 * 9999-12-01T00:00:00, — и в карточке рейтинга это печаталось как есть, в
 * строке «Дата погашения последнего кредита». Ни дата, ни её отсутствие так
 * не читаются, поэтому предельные годы показываем прочерком.
 */
export function displayDate(dateStr: string | null | undefined): string {
  if (!dateStr) return "—";
  const raw = String(dateStr).trim();
  if (!raw) return "—";
  const date = parseDate(raw.slice(0, 10));
  if (!date) return raw;
  if (date.getFullYear() >= 9999) return "—";
  return formatDateShort(raw.slice(0, 10));
}
